import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  Crown, 
  LayoutDashboard, 
  Heart, 
  BookOpen, 
  Calendar, 
  Users, 
  Sparkles,
  User,
  LogOut,
  Menu,
  X,
  Shield
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/adjust-crown', label: 'Adjust My Crown', icon: Crown },
  { path: '/tracker', label: 'No-Contact Tracker', icon: Heart },
  { path: '/modules', label: 'Healing Modules', icon: BookOpen },
  { path: '/declaration', label: 'Daily Declaration', icon: Calendar },
  { path: '/prayer-wall', label: 'Sisterhood', icon: Users },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-purple-950/50 border-r border-purple-100 dark:border-purple-800/30 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center flex-shrink-0 px-6">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center">
                <Crown className="w-6 h-6 text-purple-950" />
              </div>
              <span className="font-serif text-xl font-semibold text-foreground">Reclaiming My Crown</span>
            </Link>
          </div>
          
          <nav className="mt-8 flex-1 px-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive 
                      ? 'bg-primary text-white shadow-lg' 
                      : 'text-muted-foreground hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:text-foreground'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Premium Badge or Upgrade */}
          <div className="px-4 mt-4">
            {user?.is_premium ? (
              <div className="glass-gold rounded-xl p-4 text-center">
                <Sparkles className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">Crown Premium</p>
              </div>
            ) : (
              <Link to="/subscription">
                <div className="gold-gradient rounded-xl p-4 text-center hover:scale-[1.02] transition-transform cursor-pointer">
                  <Sparkles className="w-6 h-6 text-purple-950 mx-auto mb-2" />
                  <p className="text-sm font-bold text-purple-950">Upgrade to Premium</p>
                </div>
              </Link>
            )}
          </div>

          {/* User Section */}
          <div className="px-4 mt-4 pt-4 border-t border-purple-100 dark:border-purple-800/30">
            <Link
              to="/profile"
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                location.pathname === '/profile'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:bg-purple-50 dark:hover:bg-purple-900/30'
              }`}
            >
              <User className="w-5 h-5" />
              <span className="font-medium truncate">{user?.name}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 transition-all duration-200"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-purple-950/80 backdrop-blur-md border-b border-purple-100 dark:border-purple-800/30">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center">
              <Crown className="w-5 h-5 text-purple-950" />
            </div>
            <span className="font-serif text-lg font-semibold">My Crown</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white dark:bg-purple-950 border-b border-purple-100 dark:border-purple-800/30 shadow-lg animate-fade-in">
            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-primary text-white' 
                        : 'text-muted-foreground hover:bg-purple-50 dark:hover:bg-purple-900/30'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
              <div className="pt-4 border-t border-purple-100 dark:border-purple-800/30 mt-4">
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground"
                >
                  <User className="w-5 h-5" />
                  <span>{user?.name}</span>
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-600"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign Out</span>
                </button>
              </div>
            </nav>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="lg:pl-72">
        <div className="pt-16 lg:pt-0 min-h-screen">
          {children}
        </div>
      </main>
    </div>
  );
};
